import type { ItemId, MonsterId } from '@xox/shared'
import type { ReactNode } from 'react'

/** Symbols are drawn in a 40×40 box and scaled onto 1×1 cells. */
const S = 40

export type Sprite =
  | 'wall'
  | 'floor'
  | 'floorAlt'
  | 'stairs'
  | 'sealed'
  | 'chest'
  | 'gold'
  | 'bag'
  | 'hero'
  | MonsterId

const ART: Record<Sprite, ReactNode> = {
  wall: (
    <>
      <rect width={S} height={S} fill="var(--dg-wall)" />
      <path
        d="M0 13 H40 M0 27 H40 M13 0 V13 M27 13 V27 M13 27 V40"
        stroke="var(--dg-wall-edge)"
        strokeWidth="1.6"
      />
    </>
  ),
  floor: <rect width={S} height={S} fill="var(--dg-floor)" />,
  floorAlt: <rect width={S} height={S} fill="var(--dg-floor-alt)" />,
  stairs: (
    <>
      <rect width={S} height={S} fill="var(--dg-dark)" />
      <path
        d="M6 34 H34 M9 27 H31 M12 20 H28 M15 13 H25"
        stroke="var(--dg-stairs)"
        strokeWidth="2.4"
      />
    </>
  ),
  sealed: (
    <>
      <rect width={S} height={S} fill="var(--dg-dark)" />
      <rect x="6" y="6" width="28" height="28" rx="3" fill="var(--dg-wall)" />
      <path
        d="M12 12 L28 28 M28 12 L12 28"
        stroke="var(--dg-blood)"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </>
  ),
  chest: (
    <>
      <rect width={S} height={S} fill="var(--dg-floor)" />
      <rect x="7" y="14" width="26" height="18" rx="3" fill="var(--dg-wood)" />
      <rect x="7" y="14" width="26" height="7" rx="3" fill="var(--dg-wall-edge)" />
      <rect x="18" y="19" width="4" height="6" rx="1" fill="var(--dg-gold)" />
    </>
  ),
  gold: (
    <>
      <circle cx="16" cy="24" r="6" fill="var(--dg-gold)" />
      <circle cx="25" cy="21" r="6" fill="var(--dg-gold)" />
      <circle cx="21" cy="28" r="6" fill="var(--dg-gold)" stroke="var(--dg-wood)" strokeWidth="1" />
    </>
  ),
  bag: (
    <>
      <path d="M13 16 Q20 8 27 16 L30 32 Q20 36 10 32 Z" fill="var(--dg-wood)" />
      <path d="M14 16 H26" stroke="var(--dg-gold)" strokeWidth="2" />
      <circle cx="20" cy="25" r="3" fill="currentColor" />
    </>
  ),
  hero: (
    <>
      <ellipse cx="20" cy="36" rx="9" ry="2.5" fill="var(--dg-shadow)" opacity=".5" />
      <rect x="13" y="16" width="14" height="16" rx="4" fill="var(--dg-hero)" />
      <circle cx="20" cy="11" r="6" fill="var(--dg-bone)" />
      <path d="M14 9 Q20 3 26 9 V12 H14 Z" fill="var(--dg-steel)" />
      <path d="M27 22 L35 12" stroke="var(--dg-steel)" strokeWidth="3" strokeLinecap="round" />
    </>
  ),
  rat: (
    <>
      <ellipse cx="20" cy="26" rx="10" ry="6" fill="var(--dg-wall-edge)" />
      <circle cx="29" cy="23" r="4.5" fill="var(--dg-wall-edge)" />
      <circle cx="30.5" cy="22" r="1" fill="var(--dg-blood)" />
      <path d="M10 26 Q2 26 4 20" stroke="var(--dg-wall-edge)" strokeWidth="2" fill="none" />
    </>
  ),
  bat: (
    <>
      <path d="M4 18 Q12 8 20 18 Q28 8 36 18 Q28 22 20 30 Q12 22 4 18 Z" fill="var(--dg-shadow)" />
      <circle cx="17" cy="19" r="1.3" fill="var(--dg-blood)" />
      <circle cx="23" cy="19" r="1.3" fill="var(--dg-blood)" />
    </>
  ),
  slime: (
    <>
      <path d="M6 32 Q6 14 20 12 Q34 14 34 32 Z" fill="var(--dg-slime)" />
      <circle cx="16" cy="24" r="2" fill="var(--dg-dark)" />
      <circle cx="24" cy="24" r="2" fill="var(--dg-dark)" />
    </>
  ),
  archer: (
    <>
      <rect x="14" y="16" width="12" height="15" rx="3" fill="var(--dg-slime)" opacity=".7" />
      <circle cx="20" cy="11" r="5" fill="var(--dg-bone)" />
      <path d="M30 10 Q37 20 30 30" stroke="var(--dg-wood)" strokeWidth="2.5" fill="none" />
      <path d="M30 10 L30 30" stroke="var(--dg-bone)" strokeWidth="1" />
    </>
  ),
  golem: (
    <>
      <rect x="8" y="10" width="24" height="24" rx="5" fill="var(--dg-wall)" />
      <rect x="12" y="4" width="16" height="9" rx="3" fill="var(--dg-wall-edge)" />
      <rect x="15" y="7" width="3" height="3" fill="var(--dg-torch)" />
      <rect x="22" y="7" width="3" height="3" fill="var(--dg-torch)" />
    </>
  ),
  wraith: (
    <>
      <path
        d="M10 36 Q8 10 20 6 Q32 10 30 36 L26 30 L22 36 L18 30 L14 36 Z"
        fill="var(--dg-shadow)"
        opacity=".85"
      />
      <circle cx="16" cy="16" r="2" fill="var(--dg-frost)" />
      <circle cx="24" cy="16" r="2" fill="var(--dg-frost)" />
    </>
  ),
  ratKing: (
    <>
      <ellipse cx="20" cy="27" rx="13" ry="8" fill="var(--dg-wall-edge)" />
      <circle cx="31" cy="22" r="6" fill="var(--dg-wall-edge)" />
      <circle cx="33" cy="21" r="1.4" fill="var(--dg-blood)" />
      <path d="M26 14 L29 8 L32 14 L35 8 L37 15 Z" fill="var(--dg-gold)" />
    </>
  ),
  lich: (
    <>
      <path d="M8 36 Q8 12 20 8 Q32 12 32 36 Z" fill="var(--dg-shadow)" />
      <circle cx="20" cy="12" r="6" fill="var(--dg-bone)" />
      <circle cx="17.5" cy="11" r="1.5" fill="var(--dg-frost)" />
      <circle cx="22.5" cy="11" r="1.5" fill="var(--dg-frost)" />
      <path d="M32 6 V34" stroke="var(--dg-wood)" strokeWidth="2.5" />
      <circle cx="32" cy="5" r="3" fill="var(--dg-frost)" />
    </>
  ),
}

const SPRITES = Object.keys(ART) as Sprite[]

/** Colours that tint the generic bag symbol per item kind (via currentColor). */
export const ITEM_TINT: Record<ItemId, string> = {
  potion: 'var(--dg-blood)',
  bigPotion: 'var(--dg-blood)',
  knife: 'var(--dg-steel)',
  fireScroll: 'var(--dg-torch)',
  frostScroll: 'var(--dg-frost)',
  teleportScroll: 'var(--dg-hero)',
  revealScroll: 'var(--dg-bone)',
  sword: 'var(--dg-steel)',
  armor: 'var(--dg-steel)',
  amulet: 'var(--dg-gold)',
}

/** `<defs>` for one board; `prefix` keeps ids unique when two boards share a page. */
export function DungeonDefs({ prefix }: { prefix: string }) {
  return (
    <defs>
      {SPRITES.map((sym) => (
        <symbol key={sym} id={`${prefix}-${sym}`} viewBox={`0 0 ${S} ${S}`}>
          {ART[sym]}
        </symbol>
      ))}
    </defs>
  )
}
